<template>
  <div class="arrange">

    <header class="masthead">
      <div class="heading">
        <router-link class="back" :to="{ name: 'DisplaySettings' }">Display settings</router-link>
        <h1 class="title">
          <span class="chip">{{ countryCode }}</span>
          {{ countryName }}<span class="dot">.</span>
        </h1>
      </div>

      <div class="controls" v-if="!loading && !error">
        <label class="cols">
          <span class="cols-label">Columns</span>
          <select v-model.number="columns" class="select">
            <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>

        <span v-if="state" class="state">{{ state }}</span>
        <button class="btn" :disabled="!dirty || saving" @click="load">Undo</button>
        <button class="btn primary" :disabled="!dirty || saving" @click="save">
          {{ saving ? 'Saving…' : 'Save layout' }}
        </button>
      </div>
    </header>

    <p class="sub">
      Drag a department to move it. The boxes are the real ones, at their real
      height, so what you arrange here is what the screen shows.
    </p>

    <p v-if="loading" class="msg">Reading departments</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="isEmpty" class="msg">
      No departments in {{ countryName }} yet. Vero reads the department field on
      each person in Entra.
    </p>

    <!-- The board. Scaled down but proportional, so a column that overflows
         here overflows on the wall. -->
    <div v-else class="board" :style="{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }">
      <div
        v-for="(col, ci) in board"
        :key="ci"
        class="column"
        :class="{ target: overColumn === ci, tall: heightOf(col) > SCREEN_HEIGHT }"
        @dragover.prevent="overColumn = ci"
        @drop.prevent="dropInto(ci, col.length)"
      >
        <div
          v-for="(dept, di) in col"
          :key="dept.name"
          class="dept"
          :class="{ dragging: isDragging(ci, di) }"
          draggable="true"
          @dragstart="startDrag(ci, di)"
          @dragend="endDrag"
          @dragover.prevent.stop="overColumn = ci"
          @drop.prevent.stop="dropInto(ci, di)"
        >
          <h2 class="dept-name">{{ dept.name }}</h2>
          <ul class="people">
            <li v-for="p in dept.people" :key="p.id" class="person">{{ p.name }}</li>
          </ul>
        </div>

        <!-- The wall's bottom edge. A box below this line is off the screen,
             and seeing that while arranging is the whole point of drawing the
             real boxes. -->
        <div class="edge" :style="{ top: SCREEN_HEIGHT + 'px' }"><span>screen edge</span></div>
      </div>
    </div>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import {
  getDepartmentLayout, setDepartmentLayout, applyLayout, toPlacements
} from '@/services/DepartmentLayoutService'

const route = useRoute()

const countryCode = computed(() => String(route.params.country ?? '').toUpperCase())
const countryName = ref('')

// A stand-in for the wall's usable height, in the same scaled units the boxes
// are drawn in. Not exact -- screens differ -- but it puts the edge somewhere
// honest rather than leaving the column apparently bottomless.
const SCREEN_HEIGHT = 620

const board = ref([])
const columns = ref(6)
const savedSnapshot = ref('')

const loading = ref(true)
const error = ref(null)
const saving = ref(false)
const state = ref('')

const dragFrom = ref(null)
const overColumn = ref(null)

const isEmpty = computed(() => board.value.every((c) => c.length === 0))

const snapshot = computed(() =>
  JSON.stringify({ columns: columns.value, placements: toPlacements(board.value) })
)

const dirty = computed(() => snapshot.value !== savedSnapshot.value)

// Roughly what a box costs vertically: a heading plus a row per person. Only
// used to warn that a column has overflowed, so approximate is fine.
function heightOf(col) {
  return col.reduce((sum, d) => sum + 34 + d.people.length * 19 + 14, 0)
}

function isDragging(ci, di) {
  return dragFrom.value && dragFrom.value.ci === ci && dragFrom.value.di === di
}

function startDrag(ci, di) {
  dragFrom.value = { ci, di }
}

function endDrag() {
  dragFrom.value = null
  overColumn.value = null
}

function dropInto(ci, di) {
  const from = dragFrom.value
  if (!from) return

  const next = board.value.map((c) => [...c])
  const [moved] = next[from.ci].splice(from.di, 1)
  if (!moved) { endDrag(); return }

  // Removing from a column above the target shifts the target up by one.
  const target = from.ci === ci && from.di < di ? di - 1 : di

  next[ci].splice(Math.max(0, target), 0, moved)
  board.value = next
  endDrag()
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [hierarchy, people, layout] = await Promise.all([
      getSiteHierarchy(),
      getPeopleInCountry(countryCode.value),
      getDepartmentLayout(countryCode.value)
    ])

    countryName.value =
      hierarchy?.countries?.find((c) => c.code === countryCode.value)?.name ?? countryCode.value

    columns.value = layout.columns

    // The real people, grouped as the display groups them, so the boxes drawn
    // here are the boxes the wall draws.
    const byDept = new Map()
    for (const p of people ?? []) {
      const dept = p.department || 'Unassigned'
      if (!byDept.has(dept)) byDept.set(dept, [])
      byDept.get(dept).push(p)
    }

    const departments = [...byDept.entries()].map(([name, list]) => ({
      name,
      people: list.sort((a, b) => a.name.localeCompare(b.name))
    }))

    board.value = applyLayout(departments, layout.columns, layout.placements)
    savedSnapshot.value = snapshot.value
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function save() {
  saving.value = true
  state.value = ''

  try {
    await setDepartmentLayout(countryCode.value, columns.value, toPlacements(board.value))
    savedSnapshot.value = snapshot.value
    state.value = 'Saved'
    setTimeout(() => { if (state.value === 'Saved') state.value = '' }, 2000)
  } catch (err) {
    console.error(err)
    state.value = err.message ?? 'Could not save'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>


<style scoped>
.arrange {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --box: #2f4f7f;

  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding: 30px 0 18px;
  border-bottom: 1px solid var(--line);
}

.back {
  font-size: 13px;
  color: var(--ink-soft);
  text-decoration: none;
}

.back::before { content: "\2190"; margin-right: 7px; }
.back:hover { color: var(--ink); text-decoration: underline; }

.title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 8px 0 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.chip {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: #fff;
  padding: 4px 9px;
  border-radius: 5px;
}

.sub {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 14px 0 18px;
  max-width: 70ch;
}

/* ---------- Controls ---------- */

.controls {
  display: flex;
  align-items: center;
  gap: 14px;
  white-space: nowrap;
}

.cols {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cols-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.select {
  font-family: inherit;
  font-size: 14px;
  height: 42px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  outline: none;
}

.state { font-size: 13px; color: var(--ink-soft); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 42px;
  padding: 0 18px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:disabled { opacity: 0.35; cursor: default; }
.btn.primary { background: var(--ink); color: #fff; }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Board ---------- */

/* Columns stretch to the full height of the row rather than stopping at their
   last box.

   With align-items: start a short column's div ended right after its content,
   so the empty space below it belonged to the grid, not to the column. Dropping
   there hit nothing and the box sprang back -- which is why a department could
   be dropped BETWEEN two boxes but not below the last one. */
.board {
  display: grid;
  gap: 14px;
  align-items: stretch;
}

.column {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Tall enough to drop into even when the country has one small department in
     it, and taller than the screen-edge marker so the marker is reachable. */
  min-height: 680px;

  padding-bottom: 40px;
}

/* Where a dropped box would land. */
.column.target { background: rgba(237, 110, 1, 0.05); border-radius: 8px; }

/* ---------- Department boxes ---------- */

/* Drawn as the display draws them, not as a summary. Seeing the real height is
   the point: a list of names tells you nothing about whether the column fits. */
.dept {
  border: 2px solid var(--box);
  padding: 6px 10px 8px;
  background: #fff;
  cursor: grab;
}

.dept.dragging { opacity: 0.35; }

.dept-name {
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 5px;
  padding-bottom: 4px;
  border-bottom: 2px solid var(--box);
  color: var(--ink);
}

.people {
  list-style: none;
  margin: 0;
  padding: 0;
}

.person {
  font-size: 12px;
  line-height: 19px;
  color: #6b7684;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---------- Screen edge ---------- */

.edge {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 2px dashed #c9d2df;
  pointer-events: none;
}

.edge span {
  position: absolute;
  right: 0;
  top: 3px;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b0bac6;
}

/* A column whose boxes run past the edge. Marked rather than prevented: the
   arrangement is the person's to make, and they may be about to move something
   out of it. */
.column.tall .edge { border-top-color: var(--accent); }
.column.tall .edge span { color: var(--accent); }

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 900px) {
  .arrange { padding: 0 16px 60px; }
  .masthead { flex-direction: column; align-items: stretch; }
}
</style>
